import type { Cadeia, Celula, PalavraColocada, Regiao } from '../tipos';
import { caracteresCanonicos } from '../lexico/normalizacao';
import type { EstadoJogo } from '../motor/estados';
import { celulaNoIndice, celulasDe, regiaoDeCelulas, unirRegioes } from './coordenadas';
import { ultimaPalavra } from './grade';

/** A palavra mais recente que o jogador escreveu — as pontes são do tabuleiro. */
function ultimaBase(cadeia: Cadeia): PalavraColocada | undefined {
  for (let i = cadeia.palavras.length - 1; i >= 0; i -= 1) {
    const palavra = cadeia.palavras[i];
    if (palavra !== undefined && palavra.papel === 'base') return palavra;
  }
  return undefined;
}

/**
 * Área ativa (§3): a região que contém o draft ou a seleção atual.
 *
 * É o que a câmera segue (T04-07) e o que o botão `Voltar à palavra atual`
 * recentraliza (T07-04). Fica no núcleo porque é geometria pura e precisa
 * valer igual em teste e em tela.
 */
export function regiaoAtiva(estado: EstadoJogo): Regiao {
  const celulas: Celula[] = [];

  const tamanhoDraft = caracteresCanonicos(estado.draft).length;
  for (let i = 0; i < tamanhoDraft; i += 1) {
    celulas.push(celulaNoIndice(estado.origemDraft, 'horizontal', i));
  }

  // Em `escolher-ancora` não há draft: a área ativa é a ponte recém-escolhida.
  if (estado.fase === 'escolher-ancora') {
    const ponte = ultimaPalavra(estado.cadeia);
    if (ponte !== undefined) celulas.push(...celulasDe(ponte));
  }

  if (estado.ancora !== undefined) celulas.push(estado.ancora.celula);

  /*
   * Partida encerrada não tem draft nem âncora, e a origem da grade fica sendo
   * a primeira palavra que o jogador escreveu — que é justamente a mais velha
   * do tabuleiro. Depois de descobrir a palavra do dia, `Voltar à palavra
   * atual` levava de volta ao começo da cadeia em vez de à palavra que acabou
   * de encerrar a partida.
   *
   * A última palavra-base é o que o jogador acabou de escrever; a ponte que
   * vem depois dela é do tabuleiro, não dele. Por isso a busca ignora as
   * pontes e cai na última palavra qualquer só quando não há base nenhuma.
   */
  if (celulas.length === 0) {
    const ultima = ultimaBase(estado.cadeia) ?? ultimaPalavra(estado.cadeia);
    if (ultima !== undefined) celulas.push(...celulasDe(ultima));
  }

  if (celulas.length === 0) {
    return { colunaMin: 0, linhaMin: 0, colunaMax: 0, linhaMax: 0 };
  }

  return celulas.map((c) => regiaoDeCelulas([c])).reduce(unirRegioes);
}
